import React from 'react'
import { TP_IS_DARK, TP_MODE, TP_FONT_FAMILY, TP_FONT_SIZE, TP_FONT_WEIGHT, TP_HIGH_CONTRAST, TP_CARD_STYLE, TP_CORNER_RADIUS, TP_SIDEBAR_POSITION, TP_ICON_STYLE, TP_SPACING_DENSITY } from 'portal-modules/common/constants/keys'

// Custom color keys for localStorage
const TP_PRIMARY_COLOR = 'tp_primary_color'
const TP_SECONDARY_COLOR = 'tp_secondary_color'
const TP_ACCENT_COLOR = 'tp_accent_color'

// Safe localStorage helpers to handle private browsing mode and disabled storage
const safeLocalStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key)
    } catch (error) {
      return null
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value)
    } catch (error) {
      // Silently fail if localStorage is unavailable
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key)
    } catch (error) {
      // Silently fail if localStorage is unavailable
    }
  },
}

const initialState = {
  dark: true,
  toggle: () => { },
  fontFamily: 'sans-serif',
  setFontFamily: () => { },
  fontSize: 'medium',
  setFontSize: () => { },
  fontWeight: 'regular',
  setFontWeight: () => { },
  // Custom color states
  primaryColor: null,
  setPrimaryColor: () => { },
  secondaryColor: null,
  setSecondaryColor: () => { },
  accentColor: null,
  setAccentColor: () => { },
  resetColors: () => { },
  highContrast: false,
  setHighContrast: () => { },
  cardStyle: 'flat',
  setCardStyle: () => { },
  cornerRadius: 'rounded',
  setCornerRadius: () => { },
  sidebarPosition: 'right',
  setSidebarPosition: () => { },
  iconStyle: 'outline',
  setIconStyle: () => { },
  spacingDensity: 'comfortable',
  setSpacingDensity: () => { },
}
const TPThemeContext = React.createContext(initialState)

function TPThemeProvider({ children }) {
  const [dark, setDark] = React.useState(true) // Default theme is dark
  const [fontFamily, setFontFamilyState] = React.useState('sans-serif') // Default font family
  const [fontSize, setFontSizeState] = React.useState('medium') // Default font size
  const [fontWeight, setFontWeightState] = React.useState('regular') // Default font weight

  // Custom color states
  const [primaryColor, setPrimaryColorState] = React.useState(null) // Custom primary color
  const [secondaryColor, setSecondaryColorState] = React.useState(null) // Custom secondary color
  const [accentColor, setAccentColorState] = React.useState(null) // Custom accent color

  const [highContrast, setHighContrastState] = React.useState(false) // Default high contrast mode is off
  const [cardStyle, setCardStyleState] = React.useState('flat') // Default card style is flat
  const [cornerRadius, setCornerRadiusState] = React.useState('rounded') // Default corner radius is rounded
  const [sidebarPosition, setSidebarPositionState] = React.useState('right') // Default sidebar position is right
  const [iconStyle, setIconStyleState] = React.useState('outline') // Default icon style is outline
  const [spacingDensity, setSpacingDensityState] = React.useState('comfortable')
  const matchMedia = window.matchMedia('(prefers-color-scheme: dark)') // Get System OS mode

  // On mount, read the preferred theme from the persistence
  // Note: Empty dependency array is intentional - we only want this to run once on mount.
  // matchMedia is excluded because it's a stable reference created during render and
  // we don't want to re-run this effect if the system preference changes after mount.
  React.useEffect(() => {
    let userMode = true
    if (safeLocalStorage.getItem(TP_IS_DARK) === null) {
      // No stored preference, default to dark mode
      userMode = true
      safeLocalStorage.setItem(TP_MODE, 'dark')
      safeLocalStorage.setItem(TP_IS_DARK, 'true')
    } else if (safeLocalStorage.getItem(TP_MODE) === 'auto') {
      userMode = !!matchMedia?.matches
    } else {
      userMode = safeLocalStorage.getItem(TP_IS_DARK) === 'true'
    }
    setDark(userMode)

    // Load font family preference
    const savedFontFamily = safeLocalStorage.getItem(TP_FONT_FAMILY)
    if (savedFontFamily) {
      setFontFamilyState(savedFontFamily)
    }

    // Load font size preference
    const savedFontSize = safeLocalStorage.getItem(TP_FONT_SIZE)
    if (savedFontSize) {
      setFontSizeState(savedFontSize)
    }

    // Load font weight preference
    const savedFontWeight = safeLocalStorage.getItem(TP_FONT_WEIGHT)
    if (savedFontWeight) {
      setFontWeightState(savedFontWeight)
    }

    // Load custom color preferences
    const savedPrimaryColor = safeLocalStorage.getItem(TP_PRIMARY_COLOR)
    if (savedPrimaryColor && savedPrimaryColor !== '') {
      setPrimaryColorState(savedPrimaryColor)
    }

    const savedSecondaryColor = safeLocalStorage.getItem(TP_SECONDARY_COLOR)
    if (savedSecondaryColor && savedSecondaryColor !== '') {
      setSecondaryColorState(savedSecondaryColor)
    }

    const savedAccentColor = safeLocalStorage.getItem(TP_ACCENT_COLOR)
    if (savedAccentColor && savedAccentColor !== '') {
      setAccentColorState(savedAccentColor)
    }

    // Load high contrast preference
    const savedHighContrast = safeLocalStorage.getItem(TP_HIGH_CONTRAST)
    if (savedHighContrast) {
      setHighContrastState(savedHighContrast === 'true')
    }

    // Load card style preference
    const savedCardStyle = safeLocalStorage.getItem(TP_CARD_STYLE)
    if (savedCardStyle) {
      setCardStyleState(savedCardStyle)
    }

    // Load corner radius preference
    const savedCornerRadius = safeLocalStorage.getItem(TP_CORNER_RADIUS)
    if (savedCornerRadius) {
      setCornerRadiusState(savedCornerRadius)
    }

    // Load sidebar position preference
    const savedSidebarPosition = safeLocalStorage.getItem(TP_SIDEBAR_POSITION)
    if (savedSidebarPosition) {
      setSidebarPositionState(savedSidebarPosition)
    }

    // Load icon style preference
    const savedIconStyle = safeLocalStorage.getItem(TP_ICON_STYLE)
    if (savedIconStyle) {
      setIconStyleState(savedIconStyle)
    }

    // Load spacing density preference
    const savedSpacingDensity = safeLocalStorage.getItem(TP_SPACING_DENSITY)
    if (savedSpacingDensity) {
      setSpacingDensityState(savedSpacingDensity)
    }
  }, []) // matchMedia is intentionally excluded - it's a stable MediaQueryList object that doesn't need to trigger re-runs

  // To toggle between dark and light modes
  // Memoized to prevent unnecessary re-renders in consuming components
  const toggle = React.useCallback((mode = 'light') => {
    let isDark
    if (mode === 'dark') {
      isDark = true
    } else if (mode === 'light') {
      isDark = false
    } else {
      isDark = !!matchMedia?.matches // Set System OS mode
    }
    safeLocalStorage.setItem(TP_IS_DARK, JSON.stringify(isDark))
    safeLocalStorage.setItem(TP_MODE, mode)
    setDark(isDark)
  }, [matchMedia])

  // To change font family across the application
  // Memoized to prevent unnecessary re-renders in consuming components
  const setFontFamily = React.useCallback((font) => {
    setFontFamilyState(font)
    safeLocalStorage.setItem(TP_FONT_FAMILY, font)
  }, [])

  // To change font size across the application
  // Memoized to prevent unnecessary re-renders in consuming components
  const setFontSize = React.useCallback((size) => {
    setFontSizeState(size)
    safeLocalStorage.setItem(TP_FONT_SIZE, size)
  }, [])

  // To change font weight across the application
  // Memoized to prevent unnecessary re-renders in consuming components
  const setFontWeight = React.useCallback((weight) => {
    setFontWeightState(weight)
    safeLocalStorage.setItem(TP_FONT_WEIGHT, weight)
  }, [])

  // To change primary color across the application
  // Memoized to prevent unnecessary re-renders in consuming components
  const setPrimaryColor = React.useCallback((color) => {
    setPrimaryColorState(color)
    safeLocalStorage.setItem(TP_PRIMARY_COLOR, color)
  }, [])

  // To change secondary color across the application
  // Memoized to prevent unnecessary re-renders in consuming components
  const setSecondaryColor = React.useCallback((color) => {
    setSecondaryColorState(color)
    safeLocalStorage.setItem(TP_SECONDARY_COLOR, color)
  }, [])

  // To change accent color across the application
  // Memoized to prevent unnecessary re-renders in consuming components
  const setAccentColor = React.useCallback((color) => {
    setAccentColorState(color)
    safeLocalStorage.setItem(TP_ACCENT_COLOR, color)
  }, [])

  const setIconStyle = React.useCallback((style) => {
    setIconStyleState(style)
    safeLocalStorage.setItem(TP_ICON_STYLE, style)
  }, [])
  // To reset all custom colors to defaults
  // Memoized to prevent unnecessary re-renders in consuming components
  const resetColors = React.useCallback(() => {
    setPrimaryColorState(null)
    setSecondaryColorState(null)
    setAccentColorState(null)
    // Remove from localStorage using safe wrapper
    safeLocalStorage.removeItem(TP_PRIMARY_COLOR)
    safeLocalStorage.removeItem(TP_SECONDARY_COLOR)
    safeLocalStorage.removeItem(TP_ACCENT_COLOR)
  }, [])

  // To toggle high contrast mode across the application
  // Memoized to prevent unnecessary re-renders in consuming components
  const setHighContrast = React.useCallback((enabled) => {
    setHighContrastState(enabled)
    safeLocalStorage.setItem(TP_HIGH_CONTRAST, JSON.stringify(enabled))
  }, [])

  // To change card style across the application
  // Memoized to prevent unnecessary re-renders in consuming components
  const setCardStyle = React.useCallback((style) => {
    setCardStyleState(style)
    safeLocalStorage.setItem(TP_CARD_STYLE, style)
  }, [])

  // To change corner radius across the application
  // Memoized to prevent unnecessary re-renders in consuming components
  const setCornerRadius = React.useCallback((radius) => {
    setCornerRadiusState(radius)
    safeLocalStorage.setItem(TP_CORNER_RADIUS, radius)
  }, [])

  // To change sidebar position across the application
  // Memoized to prevent unnecessary re-renders in consuming components
  const setSidebarPosition = React.useCallback((position) => {
    setSidebarPositionState(position)
    safeLocalStorage.setItem(TP_SIDEBAR_POSITION, position)
  }, [])

  // To change spacing density across the application
  // Memoized to prevent unnecessary re-renders in consuming components
  const setSpacingDensity = React.useCallback((density) => {
    setSpacingDensityState(density)
    safeLocalStorage.setItem(TP_SPACING_DENSITY, density)
  }, [])
  // Memoize context value to prevent unnecessary re-renders
  const contextValue = React.useMemo(
    () => ({
      dark,
      toggle,
      fontFamily,
      setFontFamily,
      fontSize,
      setFontSize,
      fontWeight,
      setFontWeight,
      // Custom color values and setters
      primaryColor,
      setPrimaryColor,
      secondaryColor,
      setSecondaryColor,
      accentColor,
      setAccentColor,
      resetColors,
      highContrast,
      setHighContrast,
      cardStyle,
      setCardStyle,
      cornerRadius,
      setCornerRadius,
      sidebarPosition,
      setSidebarPosition,
      iconStyle,
      setIconStyle,
      spacingDensity,
      setSpacingDensity
    }),
    [dark, toggle, fontFamily, setFontFamily, fontSize, setFontSize, fontWeight, setFontWeight,
      primaryColor, setPrimaryColor, secondaryColor, setSecondaryColor, accentColor, setAccentColor, resetColors,
      highContrast, setHighContrast, cardStyle, setCardStyle, cornerRadius, setCornerRadius,
      sidebarPosition, setSidebarPosition, iconStyle, setIconStyle, spacingDensity, setSpacingDensity]
  )

  return (
    <TPThemeContext.Provider value={contextValue}>
      {children}
    </TPThemeContext.Provider>
  )
}

export { TPThemeProvider, TPThemeContext }
